import { useCallback, useLayoutEffect, useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'theme-preference';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The order the sidebar's button steps through. */
export const THEME_CYCLE: ThemePreference[] = ['light', 'dark', 'system'];

export const THEME_LABELS: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'Match system',
};

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function isPreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** The stored choice; `system` when there is none or storage is unavailable. */
export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
    memoryPreference = null;
  } catch {
    // Private windows and full storage: the choice holds for this page only.
    memoryPreference = preference;
  }
  notify();
}

// What the page is showing when storage refused the write.
let memoryPreference: ThemePreference | null = null;

function currentPreference(): ThemePreference {
  return memoryPreference ?? readThemePreference();
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab changing the choice, and the OS switching at sunset.
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_STORAGE_KEY || e.key === null) listener();
  };
  window.addEventListener('storage', onStorage);
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  // Safari before 14 has only the older `addListener`/`removeListener` pair on
  // a media query; without it "Match system" would apply the OS theme once and
  // never follow it again.
  if (media?.addEventListener) media.addEventListener('change', listener);
  else media?.addListener?.(listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
    if (media?.removeEventListener) media.removeEventListener('change', listener);
    else media?.removeListener?.(listener);
  };
}

/**
 * The person's theme: what they chose (`preference`) and what that comes to
 * right now (`resolved` — `system` follows the operating system, live). The
 * choice is per browser, like the sidebar's collapsed state, not per account.
 */
export function useTheme() {
  const preference = useSyncExternalStore(subscribe, currentPreference, () => 'system' as const);
  const systemDark = useSyncExternalStore(subscribe, systemPrefersDark, () => false);
  const resolved = resolveTheme(preference, systemDark);

  const setPreference = useCallback((next: ThemePreference) => writeThemePreference(next), []);
  const cycle = useCallback(() => {
    const at = THEME_CYCLE.indexOf(currentPreference());
    writeThemePreference(THEME_CYCLE[(at + 1) % THEME_CYCLE.length]);
  }, []);

  return { preference, resolved, setPreference, cycle };
}

/**
 * Puts the resolved theme on `<html>` while the calling component is mounted,
 * and takes it off again when it unmounts.
 *
 * ⚠️ **Mounted by the app shell, and only there.** The public website, the
 * RSVP pages, the vendor upload page and the questionnaire a client fills in
 * are designed as light pages with their own palettes; a producer who works in
 * dark mode must not see a guest's invitation turn dark. Scoping the class to
 * the shell's lifetime is what keeps them out of it, including after
 * navigating from the app to one of them.
 */
export function useApplyTheme(): ResolvedTheme {
  const { resolved } = useTheme();
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    return () => root.classList.remove('dark');
  }, [resolved]);
  return resolved;
}

/** Test seam: forget the in-memory fallback between tests. */
export function resetThemeMemory(): void {
  memoryPreference = null;
}
